import { useState } from "react";
import { CheckCircle2, MessageCircleHeart, Star, X } from "lucide-react";
import { toast } from "sonner";

export function FeedbackModal({
  onClose,
  title = "How was your giving experience?",
  subtitle = "Your feedback helps us improve Manavta for every donor.",
}: {
  onClose: () => void;
  title?: string;
  subtitle?: string;
}) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (rating === 0) {
      toast.error("Please select a star rating");
      return;
    }
    setSubmitted(true);
    toast.success("Thank you for sharing your feedback!");
    setTimeout(onClose, 1400);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-sm rounded-3xl border border-border bg-background p-6 text-foreground shadow-2xl">
        <div className="flex items-start justify-between">
          <div className="grid h-11 w-11 place-items-center rounded-2xl bg-[var(--saffron)]/15 text-[var(--saffron-deep)]">
            <MessageCircleHeart className="h-5.5 w-5.5" />
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-muted-foreground transition hover:text-foreground"
          >
            <X className="h-4.5 w-4.5" />
          </button>
        </div>

        {submitted ? (
          <div className="py-6 text-center">
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-[oklch(0.93_0.06_155)] text-[var(--success)]">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <p className="mt-4 font-display text-base font-bold text-[var(--maroon-deep)]">
              Thanks for your feedback!
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              It helps us make giving better for everyone.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <h3 className="mt-4 font-display text-lg font-bold text-[var(--maroon-deep)]">
              {title}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>

            <div className="mt-5 flex items-center justify-center gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(0)}
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}
                  className="p-0.5 transition hover:scale-110"
                >
                  <Star
                    className={`h-7 w-7 ${
                      n <= (hoverRating || rating)
                        ? "fill-[var(--saffron)] text-[var(--saffron-deep)]"
                        : "fill-transparent text-border"
                    }`}
                  />
                </button>
              ))}
            </div>

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Tell us what went well, or what we can improve (optional)"
              className="mt-4 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[var(--maroon)]"
            />

            <div className="mt-5 flex gap-2.5">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-full border border-border py-2.5 text-sm font-semibold text-foreground/75 transition hover:bg-muted/40"
              >
                Maybe later
              </button>
              <button
                type="submit"
                className="flex-1 rounded-full bg-[var(--maroon)] py-2.5 text-sm font-bold text-white shadow-md transition hover:bg-[var(--maroon-deep)]"
              >
                Submit Feedback
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
